import { useCallback, useEffect, useState } from "react";

/**
 * Tracks whether a scrollable container has more content below the current
 * scroll position, so callers can show a "scroll for more" affordance.
 *
 * Uses a callback ref (instead of a plain useRef) so it works correctly for
 * lazily-mounted elements such as Radix Dialog content: the check runs as
 * soon as the node actually attaches to the DOM (e.g. right when a dialog
 * opens), not only on some unrelated dependency change. A ResizeObserver
 * also keeps it accurate if the content's size changes later (images/fonts
 * loading, text reflow) without an unmount/remount.
 */
export function useScrollOverflow() {
  const [node, setNode] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const ref = useCallback((el) => setNode(el), []);

  const check = useCallback(() => {
    if (!node) return;
    setHasMore(node.scrollHeight - node.scrollTop - node.clientHeight > 24);
  }, [node]);

  useEffect(() => {
    if (!node) {
      setHasMore(false);
      return;
    }
    check();
    const observer = new ResizeObserver(check);
    observer.observe(node);
    window.addEventListener("resize", check);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", check);
    };
  }, [node, check]);

  return { ref, hasMore, onScroll: check };
}
